/**
 * Single row in the activity-log modal.
 *
 * Renders a 3-column layout (date, activity, time) with a hover-revealed
 * pencil affordance per D4. Soft-deleted entries render struck-through
 * with the existing "Entered in error" badge for audit visibility (only
 * visible when the modal toggles "Show removed entries").
 *
 * The parent owns the edit-state (only one row in edit mode at a time
 * per modal instance per D4); the row swaps to `CareTimeEntryRowEditor`
 * when `editing` is true.
 *
 * @agentic connections
 */

import {
  ActionIcon,
  Badge,
  Box,
  Group,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import type { Observation, Practitioner } from "@medplum/fhirtypes";
import { isCareTimeNonBillable } from "@benchmark/core/constants";
import {
  isAutoGeneratedCareTime,
  resolveCareTimeActivityDetail,
} from "@benchmark/core/resolvers";
import { useMedplumProfile } from "@medplum/react";
import { BillableToggle } from "./BillableToggle";
import { CareTimeEntryRowEditor } from "./CareTimeEntryRowEditor";
import { resolvePerformerDisplay } from "./resolvePerformerDisplay";
import classes from "./CareTimeEntryRow.module.css";

/** Props for `CareTimeEntryRow`. @agentic connections */
export type CareTimeEntryRowProps = {
  observation: Observation;
  patientDisplay: string;
  /** When true, the row renders the inline editor instead of the display row. */
  editing?: boolean;
  /** Called when the user clicks the row's pencil affordance. */
  onRequestEdit?: () => void;
  /** Called when the inline editor commits or cancels. */
  onEditDone?: () => void;
  /** Called when the inline editor's local state becomes dirty/clean. */
  onEditDirtyChange?: (dirty: boolean) => void;
};

const formatLongDate = (iso: string | undefined): string => {
  if (!iso) return "—";
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  return new Date(ms).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

const formatTimeStamp = (iso: string | undefined): string | undefined => {
  if (!iso) return undefined;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return undefined;
  const d = new Date(ms);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/**
 * Single care-time entry row with hover-revealed inline edit.
 * @agentic connections
 */
export const CareTimeEntryRow = ({
  observation,
  patientDisplay,
  editing = false,
  onRequestEdit,
  onEditDone,
  onEditDirtyChange,
}: CareTimeEntryRowProps) => {
  const profile = useMedplumProfile() as Practitioner | undefined;

  const isDeleted = observation.status === "entered-in-error";
  // Billable status is only editable on active, persisted entries — never on
  // soft-deleted (`entered-in-error`) or `cancelled` ones. Mirrors the billing
  // minute log's guard so both surfaces treat the same rows as toggleable.
  const canToggleBillable =
    !isDeleted && observation.status !== "cancelled" && Boolean(observation.id);
  const nonBillable = isCareTimeNonBillable(observation);
  const minutes = observation.valueQuantity?.value ?? 0;
  const effective =
    observation.effectiveDateTime ?? observation.effectivePeriod?.start;
  const activity = resolveCareTimeActivityDetail(observation);
  const context = activity.contextLabels.join(" · ");
  const notes = activity.note;
  const isAutoCreated = isAutoGeneratedCareTime(observation);

  const patientId = observation.subject?.reference?.startsWith("Patient/")
    ? observation.subject.reference.slice("Patient/".length)
    : "";

  const performerRoleId = observation.performer?.[0]?.reference?.startsWith(
    "PractitionerRole/",
  )
    ? observation.performer[0].reference.slice("PractitionerRole/".length)
    : (profile?.id ?? "");
  const performerDisplay = resolvePerformerDisplay(observation, profile);

  if (editing && !isDeleted && observation.id) {
    return (
      <CareTimeEntryRowEditor
        observation={observation}
        patientId={patientId}
        patientDisplay={patientDisplay}
        performerRoleId={performerRoleId}
        performerDisplay={performerDisplay}
        onCancel={() => onEditDone?.()}
        onSaved={() => onEditDone?.()}
        onRemoved={() => onEditDone?.()}
        onDirtyChange={onEditDirtyChange}
      />
    );
  }

  return (
    <Group
      gap="md"
      wrap="nowrap"
      align="flex-start"
      className={classes.row}
      data-deleted={isDeleted ? "true" : undefined}
    >
      <Stack gap={0} style={{ minWidth: 100 }}>
        <Text size="sm" fw={600} td={isDeleted ? "line-through" : undefined}>
          {formatLongDate(effective)}
        </Text>
        <Text size="xs" c="dimmed">
          {formatTimeStamp(effective) ?? ""}
        </Text>
      </Stack>
      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" wrap="wrap">
          <Badge size="xs" variant="light" color="teal">
            {activity.methodLabel}
          </Badge>
          {isAutoCreated ? (
            <Badge size="xs" variant="light" color="blue">
              Auto
            </Badge>
          ) : null}
          {isDeleted ? (
            <Badge size="xs" variant="light" color="red">
              Entered in error
            </Badge>
          ) : null}
        </Group>
        {context && (
          <Text
            size="xs"
            c="dimmed"
            td={isDeleted ? "line-through" : undefined}
          >
            {context}
          </Text>
        )}
        {notes ? (
          <Text
            size="sm"
            c="dimmed"
            lineClamp={2}
            td={isDeleted ? "line-through" : undefined}
          >
            {notes}
          </Text>
        ) : null}
      </Stack>
      <Stack gap={4} align="flex-end" style={{ minWidth: 120 }}>
        <Group gap={6} align="center" justify="flex-end" wrap="nowrap">
          <Text
            size="sm"
            fw={600}
            c={nonBillable && !isDeleted ? "dimmed" : undefined}
            td={isDeleted ? "line-through" : undefined}
            title={
              nonBillable && !isDeleted
                ? "Excluded from CoCM billing"
                : undefined
            }
          >
            {minutes} min
          </Text>
          {!isDeleted && onRequestEdit ? (
            <Box className={classes.pencil}>
              <Tooltip label="Edit entry" withArrow>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="gray"
                  onClick={onRequestEdit}
                  aria-label="Edit care time entry"
                >
                  <IconPencil size={14} />
                </ActionIcon>
              </Tooltip>
            </Box>
          ) : null}
        </Group>
        {canToggleBillable ? (
          <BillableToggle observation={observation} />
        ) : null}
      </Stack>
    </Group>
  );
};
